import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import {
  OrganizationMember,
  OrganizationRole,
} from '../../services/organizationApi';

interface TransferOwnershipDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  members: OrganizationMember[];
  newOwnerId: string;
  setNewOwnerId: (id: string) => void;
  loading: boolean;
}

const TransferOwnershipDialog: React.FC<TransferOwnershipDialogProps> = ({
  open,
  onClose,
  onSubmit,
  members,
  newOwnerId,
  setNewOwnerId,
  loading,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  // Filter out current owner and only show eligible members (admins)
  const eligibleMembers = members.filter(member => 
    member.role === OrganizationRole.ADMIN && member.id !== newOwnerId
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Transfer Ownership</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action will transfer full ownership of the organization to another member. 
              You will become a regular member and lose administrative privileges.
            </Alert>

            <Typography variant="body1" gutterBottom>
              Select the new owner for this organization:
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel>New Owner</InputLabel>
              <Select
                value={newOwnerId}
                label="New Owner"
                onChange={(e) => setNewOwnerId(e.target.value)}
                disabled={loading}
                required
              >
                {eligibleMembers.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.user.username} ({member.user.email}) - {member.role === 10 ? 'Admin' : 'Member'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>Note:</strong> Only organization admins can be selected as new owners. 
              The new owner will have full control over the organization, including the ability to remove members and delete the organization.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="warning"
            disabled={loading || !newOwnerId}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Transferring...' : 'Transfer Ownership'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TransferOwnershipDialog;
